'use client';

import clsx from 'clsx';
import { useEffect, useRef, type MutableRefObject, type Ref } from 'react';

import { InlineErrorAlert } from './InlineErrorAlert';
import styles from './InlineErrorAlert.module.scss';

export interface InlineErrorAlertListProps {
  /** Error messages to display as individual banners */
  errors: readonly string[];
  /** Container id — individual alerts use `${id}-${index}` */
  id?: string;
  className?: string;
  /** When false, errors stay visible but are not announced (avoids duplicate live regions). */
  announce?: boolean;
  /** Increment after a failed submit to scroll errors into view */
  scrollToken?: number;
  /** Optional external ref to the error list container (for parent scroll-to-error). */
  containerRef?: Ref<HTMLDivElement>;
  'data-testid'?: string;
}

function assignRef(ref: Ref<HTMLDivElement> | undefined, node: HTMLDivElement | null): void {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  (ref as MutableRefObject<HTMLDivElement | null>).current = node;
}

/**
 * Renders one or more InlineErrorAlert banners and optionally scrolls them into view
 * after a failed form submission.
 */
export function InlineErrorAlertList({
  errors,
  id = 'validation-errors',
  className,
  announce = true,
  scrollToken = 0,
  containerRef,
  'data-testid': testId,
}: Readonly<InlineErrorAlertListProps>) {
  const localRef = useRef<HTMLDivElement>(null);

  const setRefs = (node: HTMLDivElement | null) => {
    assignRef(localRef, node);
    assignRef(containerRef, node);
  };

  useEffect(() => {
    if (scrollToken === 0 || errors.length === 0) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      localRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      localRef.current?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frameId);
  }, [scrollToken, errors]);

  if (errors.length === 0) {
    return null;
  }

  return (
    <div
      ref={setRefs}
      id={id}
      className={clsx(styles.list, className)}
      tabIndex={-1}
      aria-live={announce ? 'polite' : undefined}
      aria-hidden={announce ? undefined : true}
      data-testid={testId}
    >
      {errors.map((message, index) => (
        <InlineErrorAlert key={`${index}-${message}`} id={`${id}-${index}`}>
          {message}
        </InlineErrorAlert>
      ))}
    </div>
  );
}
